import { useEffect, useState } from "react";
import { Wrench, AlertTriangle, TrendingDown, CheckCircle2, Clock, RefreshCw, ShoppingCart } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  ok: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  warning: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  urgent: "bg-orange-500/15 border-orange-500/40 text-orange-300",
  overdue: "bg-rose-500/15 border-rose-500/40 text-rose-300",
  critical: "bg-rose-500/15 border-rose-500/40 text-rose-300",
  sell_recommended: "bg-purple-500/15 border-purple-500/40 text-purple-300",
};

const ICONS = {
  critical: AlertTriangle,
  urgent: Clock,
  warning: Wrench,
  sell_recommended: ShoppingCart,
  ok: CheckCircle2,
};

export default function PredictiveMaintenance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/predictive/maintenance/fleet/all");
      setData(r.data);
    } catch (e) { toast.error("Errore caricamento"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = data?.predictions?.filter(p => filter === "all" ? true : p.overall === filter) || [];

  return (
    <div className="space-y-6" data-testid="predictive-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            Predictive Maintenance <Wrench className="text-[#ff0033]" />
          </h1>
          <p className="text-sm text-white/45 mt-1">Predice prossimi interventi su base km · storia · età veicolo</p>
        </div>
        <button onClick={load} data-testid="predict-refresh" disabled={loading}
          className="px-4 py-2 rounded-full text-xs bg-white/5 border border-white/10 hover:bg-white/10 inline-flex items-center gap-2">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Ricalcola
        </button>
      </div>

      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { k: "critical", label: "Overdue / Critical", icon: AlertTriangle, c: "text-rose-400" },
            { k: "urgent", label: "Urgenti", icon: Clock, c: "text-orange-400" },
            { k: "warning", label: "Warning", icon: Wrench, c: "text-amber-400" },
            { k: "sell_recommended", label: "Sell Alert", icon: ShoppingCart, c: "text-purple-400" },
            { k: "ok", label: "OK", icon: CheckCircle2, c: "text-emerald-400" },
          ].map((s) => {
            const Ic = s.icon;
            return (
              <button key={s.k} data-testid={`predict-filter-${s.k}`} onClick={() => setFilter(filter === s.k ? "all" : s.k)}
                className={`bg-white/[0.02] border rounded-2xl p-4 text-left transition ${filter === s.k ? "border-[#ff0033]" : "border-white/10 hover:border-white/20"}`}>
                <div className="flex items-center gap-2 mb-2"><Ic size={14} className={s.c} /><span className="text-[10px] uppercase tracking-widest text-white/40">{s.label}</span></div>
                <div className="text-2xl font-black">{data.summary[s.k] || 0}</div>
              </button>
            );
          })}
        </div>
      )}

      <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-black/40 text-white/60 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Veicolo</th>
              <th className="text-left p-3">Km attuali</th>
              <th className="text-left p-3">Stato</th>
              <th className="text-left p-3">Predizioni</th>
              <th className="text-left p-3">Sell Alert</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-white/40 text-xs">Nessuna previsione</td></tr>}
            {filtered.map((p) => {
              const Ic = ICONS[p.overall] || Wrench;
              return (
                <tr key={p.car_id} className="border-t border-white/5" data-testid={`predict-row-${p.car_id}`}>
                  <td className="p-3">
                    <div className="font-medium">{p.marca} {p.modello}</div>
                    <div className="text-[10px] text-white/40 font-mono">{p.targa}</div>
                  </td>
                  <td className="p-3 font-mono text-xs">{p.km_now?.toLocaleString()} km</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase inline-flex items-center gap-1 ${STATUS_COLORS[p.overall]}`}><Ic size={11}/> {p.overall}</span></td>
                  <td className="p-3 text-xs">
                    <div className="flex flex-wrap gap-1">
                      {p.predictions?.filter(pr => pr.status !== "ok").slice(0,4).map((pr, i) => (
                        <span key={i} className={`px-2 py-0.5 rounded text-[10px] border ${STATUS_COLORS[pr.status]}`}>
                          {pr.kind.replace(/_/g, " ")} · {pr.delta_km !== undefined ? `${pr.delta_km}km` : pr.delta_days ? `${pr.delta_days}gg` : ""}
                        </span>
                      ))}
                      {p.predictions?.every(pr => pr.status === "ok") && (
                        <span className="text-emerald-400 text-[10px]">Tutti gli intervalli OK</span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-xs">
                    {p.sell_alert ? (
                      <div className="text-purple-300">
                        <div className="font-semibold flex items-center gap-1"><TrendingDown size={11}/> {p.sell_alert.recommendation}</div>
                        <div className="text-[10px] text-white/40 mt-0.5">{p.sell_alert.reason}</div>
                      </div>
                    ) : <span className="text-white/30 text-[10px]">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
